import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { shade } from "polished";
import * as React from "react";
import { useState, useEffect, forwardRef, useRef, useCallback } from "react";
import styled, { css } from "styled-components";
import { colors, typographyCss, cbSemanticCssVars, semanticColors } from "@creditornot/cb-ingredients";
import { Spinner } from "@creditornot/cb-progress";
import { createCssVars, createCssVarDefinitions, createThemedCssVarDefinitions, themedConfigToThemedAttrs } from "@creditornot/cb-utils";
import { useFloating, shift, offset, flip, autoUpdate, useClick, useDismiss, useRole, useListNavigation, useTypeahead, useInteractions, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { MenuItemGroup, MenuActionItem } from "@creditornot/cb-menu";
import { getDefaultGlobalPopoverMountElement, PopoverBase, usePopoverTransition, getClosedStyles } from "@creditornot/cb-popover";
import { useDirection } from "@creditornot/cb-provider";
const Root$5 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${({ $show }) => `${$show ? "fadeIn" : "fadeOut"} 300ms forwards, ${$show ? "zoomIn" : "zoomOut"} 300ms forwards`};
`;
const FadeTransition = ({
  children,
  show
}) => {
  const [render, setRender] = useState(show);
  useEffect(() => {
    if (show) {
      setRender(true);
    }
  }, [show]);
  const onAnimationEnd = () => {
    if (!show) {
      setRender(false);
    }
  };
  return render ? /* @__PURE__ */ jsx(Root$5, { $show: show, onAnimationEnd, children }) : null;
};
const sizeConfigs = {
  tiny: {
    minHeight: 24,
    verticalPadding: 4,
    horizontalPadding: 8,
    stretchModeHorizontalPadding: 8,
    iconMargin: 4,
    iconWidth: 12,
    spinnerSize: 14
  },
  small: {
    minHeight: 36,
    verticalPadding: 8,
    horizontalPadding: 16,
    stretchModeHorizontalPadding: 16,
    iconMargin: 8,
    iconWidth: 12,
    spinnerSize: 18
  },
  large: {
    minHeight: 56,
    verticalPadding: 8,
    horizontalPadding: 32,
    stretchModeHorizontalPadding: 24,
    iconMargin: 12,
    iconWidth: 16,
    spinnerSize: 24
  }
};
const cssVars$1 = createCssVars([
  "minHeight",
  "verticalPadding",
  "horizontalPadding",
  "stretchModeHorizontalPadding",
  "iconMargin",
  "iconWidth",
  "spinnerSize"
]);
const variantConfigs$3 = {
  blue: {
    light: {
      color: colors.salt100,
      background: colors.wolt100,
      backdrop: "transparent",
      backgroundOnHover: colors.wolt92,
      backgroundOnActive: colors.wolt84,
      backgroundDisabled: colors.wolt64,
      colorDisabled: colors.salt48,
      fontWeight: "700"
    },
    lightHighContrast: {
      color: colors.salt100,
      background: colors.darkSky100,
      backdrop: "transparent",
      backgroundOnHover: colors.darkSky92,
      backgroundOnActive: colors.darkSky84,
      backgroundDisabled: colors.darkSky64,
      colorDisabled: colors.salt48,
      fontWeight: "700"
    },
    dark: {
      color: colors.space100,
      background: colors.wolt100,
      backdrop: "transparent",
      backgroundOnHover: colors.wolt88,
      backgroundOnActive: colors.wolt76,
      backgroundDisabled: colors.wolt60,
      colorDisabled: colors.space48,
      fontWeight: "700"
    }
  },
  lightBlue: {
    light: {
      color: colors.wolt100,
      background: colors.wolt8,
      backdrop: "transparent",
      backgroundOnHover: colors.wolt12,
      backgroundOnActive: colors.wolt24,
      backgroundDisabled: colors.wolt8,
      colorDisabled: colors.wolt40,
      fontWeight: "500"
    },
    lightHighContrast: {
      color: shade(0.1)(colors.darkSky100),
      background: colors.darkSky8,
      backdrop: "transparent",
      backgroundOnHover: colors.darkSky12,
      backgroundOnActive: colors.darkSky24,
      backgroundDisabled: colors.darkSky8,
      colorDisabled: colors.darkSky40,
      fontWeight: "500"
    },
    dark: {
      color: colors.wolt100,
      background: colors.wolt12,
      backdrop: "transparent",
      backgroundOnHover: colors.wolt16,
      backgroundOnActive: colors.wolt28,
      backgroundDisabled: colors.wolt12,
      colorDisabled: colors.wolt48,
      fontWeight: "500"
    }
  },
  lightRed: {
    light: {
      color: colors.strawberry100,
      background: colors.strawberry8,
      backdrop: "transparent",
      backgroundOnHover: colors.strawberry12,
      backgroundOnActive: colors.strawberry24,
      backgroundDisabled: colors.strawberry8,
      colorDisabled: colors.strawberry40,
      fontWeight: "700"
    },
    lightHighContrast: {
      color: shade(0.17)(colors.strawberry100),
      background: colors.strawberry8,
      backdrop: "transparent",
      backgroundOnHover: colors.strawberry12,
      backgroundOnActive: colors.strawberry24,
      backgroundDisabled: colors.strawberry8,
      colorDisabled: colors.strawberry40,
      fontWeight: "700"
    },
    dark: {
      color: colors.strawberry100,
      background: colors.strawberry12,
      backdrop: "transparent",
      backgroundOnHover: colors.strawberry16,
      backgroundOnActive: colors.strawberry28,
      backgroundDisabled: colors.strawberry12,
      colorDisabled: colors.strawberry48,
      fontWeight: "700"
    }
  },
  white: {
    light: {
      color: colors.pepper64,
      background: colors.salt100,
      backdrop: colors.salt100,
      backgroundOnHover: colors.pepper8,
      backgroundOnActive: colors.pepper12,
      backgroundDisabled: colors.salt100,
      colorDisabled: colors.pepper40,
      fontWeight: "500"
    },
    lightHighContrast: {
      color: colors.pepper80,
      background: colors.salt100,
      backdrop: colors.salt100,
      backgroundOnHover: colors.pepper8,
      backgroundOnActive: colors.pepper12,
      backgroundDisabled: colors.salt100,
      colorDisabled: colors.pepper56,
      fontWeight: "500"
    },
    dark: {
      color: colors.salt100,
      background: colors.space100,
      backdrop: colors.space100,
      backgroundOnHover: colors.salt12,
      backgroundOnActive: colors.salt16,
      backgroundDisabled: colors.space88,
      colorDisabled: colors.salt48,
      fontWeight: "500"
    }
  }
};
const spinnerColorConfig = {
  blue: "white",
  lightBlue: "blue",
  lightRed: "red",
  white: "grey"
};
const variantCssVars$3 = createCssVars([
  "color",
  "background",
  "backdrop",
  "backgroundOnHover",
  "backgroundOnActive",
  "backgroundDisabled",
  "colorDisabled",
  "fontWeight"
]);
const StretchModeIconContainer = styled.div`
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 150ms ease-in;

  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: ${({ $position }) => $position === "left" ? "flex-start" : "flex-end"};
  padding: ${cssVars$1.verticalPadding} ${cssVars$1.stretchModeHorizontalPadding};
`;
const IconContainer$2 = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-inline-start: ${({ $position }) => $position === "left" ? 0 : cssVars$1.iconMargin};
  margin-inline-end: ${({ $position }) => $position === "right" ? 0 : cssVars$1.iconMargin};
`;
const BG$1 = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
const Root$4 = styled.button`
  ${({ $cssVarOverrides, $size }) => createCssVarDefinitions({
  minHeight: `${sizeConfigs[$size].minHeight}px`,
  verticalPadding: `${sizeConfigs[$size].verticalPadding}px`,
  horizontalPadding: `${sizeConfigs[$size].horizontalPadding}px`,
  stretchModeHorizontalPadding: `${sizeConfigs[$size].stretchModeHorizontalPadding}px`,
  iconMargin: `${sizeConfigs[$size].iconMargin}px`,
  iconWidth: `${sizeConfigs[$size].iconWidth}px`,
  spinnerSize: `${sizeConfigs[$size].spinnerSize}px`,
  ...$cssVarOverrides
})}

  ${({ $variant }) => createThemedCssVarDefinitions(
  themedConfigToThemedAttrs(variantConfigs$3[$variant])
)}

  /* normalize */
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  border: none;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  box-sizing: border-box;
  position: relative;
  z-index: 0;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;

  ${({ $size }) => {
  switch ($size) {
    case "tiny":
      return typographyCss.Small();
    case "small":
      return typographyCss.Body3();
    case "large":
      return typographyCss.Body2();
  }
}}

  width: ${({ $stretch }) => $stretch ? "100%" : "auto"};
  max-width: 100%;

  cursor: pointer;
  padding: ${cssVars$1.verticalPadding} ${cssVars$1.horizontalPadding};
  font-weight: ${variantCssVars$3.fontWeight};
  min-height: ${cssVars$1.minHeight};
  border-radius: 8px;

  background: ${variantCssVars$3.backdrop};

  ${BG$1} {
    background: ${variantCssVars$3.background};
  }
  &:disabled ${BG$1} {
    background: ${variantCssVars$3.backgroundDisabled};
  }

  color: ${variantCssVars$3.color};

  &:disabled {
    color: ${variantCssVars$3.colorDisabled};
    cursor: not-allowed;
  }

  ${IconContainer$2}, ${StretchModeIconContainer} {
    svg {
      width: ${cssVars$1.iconWidth};
      fill: ${variantCssVars$3.color};
    }
  }

  &:disabled ${IconContainer$2}, &:disabled ${StretchModeIconContainer} {
    > svg {
      fill: ${variantCssVars$3.colorDisabled};
    }
  }

  &:hover:not(:disabled) ${BG$1} {
    background: ${variantCssVars$3.backgroundOnHover};
  }
  &:active:not(:disabled) ${BG$1} {
    background: ${variantCssVars$3.backgroundOnActive};
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: 3px transparent solid;
    box-shadow: 0 0 0 3px ${cbSemanticCssVars.a11yFocusIndicator};
  }

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @keyframes zoomIn {
    0% {
      transform: scale(0.4);
    }

    75% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes zoomOut {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.4);
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;
const Content = styled.div`
  z-index: 1;
  display: flex;
  align-items: center;

  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity 150ms ease-in;

  overflow: hidden;

  padding: ${({ $stretch, $withIcon }) => {
  if ($stretch && $withIcon) {
    return `0px calc(${cssVars$1.iconWidth} * 1.5)`;
  }
  return "0px";
}};
`;
const Label = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SpinnerContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;
`;
const Button = React.forwardRef(
  ({
    children,
    cssVarOverrides,
    disabled = false,
    icon,
    iconPosition = "left",
    loading,
    onClick,
    role = "button",
    size = "small",
    stretch = false,
    type = "button",
    variant = "blue",
    ...restButtonProps
  }, ref) => /* @__PURE__ */ jsxs(
    Root$4,
    {
      $cssVarOverrides: cssVarOverrides,
      $size: size,
      $stretch: stretch,
      $variant: variant,
      "aria-busy": loading,
      disabled,
      onClick: disabled ? void 0 : onClick,
      role,
      type,
      ...restButtonProps,
      ref,
      children: [
        /* @__PURE__ */ jsx(BG$1, {}),
        /* @__PURE__ */ jsx(SpinnerContainer, { children: /* @__PURE__ */ jsx(FadeTransition, { show: !!loading, children: /* @__PURE__ */ jsx(
          Spinner,
          {
            color: spinnerColorConfig[variant],
            cssVarOverrides: { size: `${sizeConfigs[size].spinnerSize}px` },
            size: "tiny"
          }
        ) }) }),
        stretch && /* @__PURE__ */ jsx(StretchModeIconContainer, { $position: iconPosition, $visible: !loading, children: icon }),
        /* @__PURE__ */ jsxs(Content, { $stretch: stretch, $visible: !loading, $withIcon: !!icon, children: [
          !stretch && !!icon && iconPosition === "left" && /* @__PURE__ */ jsx(IconContainer$2, { $position: iconPosition, children: icon }),
          /* @__PURE__ */ jsx(Label, { children }),
          !stretch && !!icon && iconPosition === "right" && /* @__PURE__ */ jsx(IconContainer$2, { $position: iconPosition, children: icon })
        ] })
      ]
    }
  )
);
Button.displayName = "Button";
const cssVars = createCssVars(["size", "iconSize", "width", "borderRadius"]);
const variantCssVars$2 = createCssVars([
  "bg",
  "bgHover",
  "bgActive",
  "bgDisabled",
  "iconFill",
  "iconFillHover",
  "iconFillDisabled"
]);
const variantConfigs$2 = {
  blue: {
    static: {
      light: {
        bg: colors.wolt8,
        bgHover: colors.wolt16,
        bgActive: colors.wolt24,
        bgDisabled: colors.wolt8,
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt40
      },
      lightHighContrast: {
        bg: colors.darkSky8,
        bgHover: colors.darkSky16,
        bgActive: colors.darkSky20,
        bgDisabled: colors.darkSky8,
        iconFill: colors.darkSky100,
        iconFillHover: colors.darkSky100,
        iconFillDisabled: colors.darkSky40
      },
      dark: {
        bg: colors.wolt16,
        bgHover: colors.wolt24,
        bgActive: colors.wolt28,
        bgDisabled: colors.wolt20,
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt48
      }
    },
    staticWhite: {
      light: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.wolt8,
        bgActive: colors.wolt16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt40
      },
      lightHighContrast: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.wolt8,
        bgActive: colors.wolt16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: shade(0.1)(colors.wolt100),
        iconFillHover: shade(0.1)(colors.wolt100),
        iconFillDisabled: shade(0.1)(colors.wolt40)
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.wolt24,
        bgActive: colors.wolt28,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt52
      }
    },
    animated: {
      light: {
        bg: colors.wolt8,
        bgHover: colors.wolt8,
        bgActive: colors.wolt16,
        bgDisabled: "transparent",
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt40
      },
      lightHighContrast: {
        bg: colors.wolt8,
        bgHover: colors.wolt8,
        bgActive: colors.wolt16,
        bgDisabled: "transparent",
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt40
      },
      dark: {
        bg: colors.wolt16,
        bgHover: colors.wolt16,
        bgActive: colors.wolt24,
        bgDisabled: "transparent",
        iconFill: colors.wolt100,
        iconFillHover: colors.wolt100,
        iconFillDisabled: colors.wolt52
      }
    }
  },
  black: {
    static: {
      light: {
        bg: colors.pepper8,
        bgHover: colors.pepper16,
        bgActive: colors.pepper24,
        bgDisabled: colors.pepper8,
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: colors.pepper8,
        bgHover: colors.pepper16,
        bgActive: colors.pepper24,
        bgDisabled: colors.pepper8,
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: colors.salt12,
        bgHover: colors.salt20,
        bgActive: colors.salt28,
        bgDisabled: colors.salt12,
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt44
      }
    },
    staticWhite: {
      light: {
        bg: colors.salt100,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.salt100,
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: colors.salt100,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.salt100,
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.salt12,
        bgActive: colors.salt20,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt44
      }
    },
    animated: {
      light: {
        bg: colors.pepper8,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: "transparent",
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: colors.pepper8,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: "transparent",
        iconFill: colors.pepper100,
        iconFillHover: colors.pepper100,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: colors.salt12,
        bgHover: colors.salt12,
        bgActive: colors.salt20,
        bgDisabled: "transparent",
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt44
      }
    }
  },
  grey: {
    static: {
      light: {
        bg: colors.pepper8,
        bgHover: colors.pepper16,
        bgActive: colors.pepper24,
        bgDisabled: colors.pepper8,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper80,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: colors.pepper8,
        bgHover: colors.pepper16,
        bgActive: colors.pepper24,
        bgDisabled: colors.pepper8,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper80,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: colors.salt12,
        bgHover: colors.salt20,
        bgActive: colors.salt28,
        bgDisabled: colors.salt12,
        iconFill: colors.salt72,
        iconFillHover: colors.salt92,
        iconFillDisabled: colors.salt48
      }
    },
    staticWhite: {
      light: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper88,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper88,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.salt20,
        bgActive: colors.salt28,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: colors.salt76,
        iconFillHover: colors.salt92,
        iconFillDisabled: colors.salt52
      }
    },
    animated: {
      light: {
        bg: colors.pepper8,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper88,
        iconFillDisabled: colors.pepper40
      },
      lightHighContrast: {
        bg: colors.pepper8,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper64,
        iconFillHover: colors.pepper88,
        iconFillDisabled: colors.pepper40
      },
      dark: {
        bg: colors.salt12,
        bgHover: colors.salt12,
        bgActive: colors.salt20,
        bgDisabled: colors.salt8,
        iconFill: colors.salt68,
        iconFillHover: colors.salt92,
        iconFillDisabled: colors.salt44
      }
    }
  },
  lightGrey: {
    static: {
      light: {
        bg: colors.pepper4,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      lightHighContrast: {
        bg: colors.pepper4,
        bgHover: colors.pepper8,
        bgActive: colors.pepper16,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      dark: {
        bg: colors.salt8,
        bgHover: colors.salt12,
        bgActive: colors.salt20,
        bgDisabled: colors.salt8,
        iconFill: colors.salt56,
        iconFillHover: colors.salt80,
        iconFillDisabled: colors.salt40
      }
    },
    staticWhite: {
      light: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.pepper4,
        bgActive: colors.pepper12,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      lightHighContrast: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.pepper4,
        bgActive: colors.pepper12,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.salt16,
        bgActive: colors.salt16,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: colors.salt60,
        iconFillHover: colors.salt84,
        iconFillDisabled: colors.salt44
      }
    },
    animated: {
      light: {
        bg: colors.pepper4,
        bgHover: colors.pepper4,
        bgActive: colors.pepper12,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      lightHighContrast: {
        bg: colors.pepper4,
        bgHover: colors.pepper4,
        bgActive: colors.pepper12,
        bgDisabled: colors.pepper4,
        iconFill: colors.pepper52,
        iconFillHover: colors.pepper76,
        iconFillDisabled: colors.pepper36
      },
      dark: {
        bg: colors.salt16,
        bgHover: colors.salt16,
        bgActive: colors.salt24,
        bgDisabled: colors.salt16,
        iconFill: colors.salt60,
        iconFillHover: colors.salt84,
        iconFillDisabled: colors.salt44
      }
    }
  },
  white: {
    static: {
      light: {
        bg: colors.salt8,
        bgHover: colors.salt16,
        bgActive: colors.salt24,
        bgDisabled: colors.salt8,
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt40
      },
      lightHighContrast: {
        bg: colors.salt8,
        bgHover: colors.salt16,
        bgActive: colors.salt24,
        bgDisabled: colors.salt8,
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt40
      },
      dark: {
        bg: colors.space8,
        bgHover: colors.space16,
        bgActive: colors.space24,
        bgDisabled: colors.space8,
        iconFill: colors.space100,
        iconFillHover: colors.space100,
        iconFillDisabled: colors.space40
      }
    },
    staticWhite: {
      light: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.salt8,
        bgActive: colors.salt16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: semanticColors.light.surfaceMain,
        iconFillHover: semanticColors.light.surfaceMain,
        iconFillDisabled: colors.salt40
      },
      lightHighContrast: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.salt8,
        bgActive: colors.salt16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: semanticColors.light.surfaceMain,
        iconFillHover: semanticColors.light.surfaceMain,
        iconFillDisabled: colors.salt40
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.space8,
        bgActive: colors.space16,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: semanticColors.dark.surfaceMain,
        iconFillHover: semanticColors.dark.surfaceMain,
        iconFillDisabled: colors.space40
      }
    },
    animated: {
      light: {
        bg: colors.salt8,
        bgHover: colors.salt8,
        bgActive: colors.salt16,
        bgDisabled: "transparent",
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt40
      },
      lightHighContrast: {
        bg: colors.salt8,
        bgHover: colors.salt8,
        bgActive: colors.salt16,
        bgDisabled: "transparent",
        iconFill: colors.salt100,
        iconFillHover: colors.salt100,
        iconFillDisabled: colors.salt40
      },
      dark: {
        bg: colors.space8,
        bgHover: colors.space8,
        bgActive: colors.space16,
        bgDisabled: "transparent",
        iconFill: colors.space100,
        iconFillHover: colors.space100,
        iconFillDisabled: colors.space40
      }
    }
  },
  red: {
    static: {
      light: {
        bg: colors.strawberry8,
        bgHover: colors.strawberry16,
        bgActive: colors.strawberry24,
        bgDisabled: colors.strawberry8,
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry40
      },
      lightHighContrast: {
        bg: colors.strawberry8,
        bgHover: colors.strawberry16,
        bgActive: colors.strawberry24,
        bgDisabled: colors.strawberry8,
        iconFill: shade(0.17)(colors.strawberry100),
        iconFillHover: shade(0.17)(colors.strawberry100),
        iconFillDisabled: shade(0.17)(colors.strawberry40)
      },
      dark: {
        bg: colors.strawberry16,
        bgHover: colors.strawberry20,
        bgActive: colors.strawberry24,
        bgDisabled: colors.strawberry20,
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry48
      }
    },
    staticWhite: {
      light: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.strawberry8,
        bgActive: colors.strawberry16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry40
      },
      lightHighContrast: {
        bg: semanticColors.light.surfaceMain,
        bgHover: colors.strawberry8,
        bgActive: colors.strawberry16,
        bgDisabled: semanticColors.light.surfaceMain,
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry40
      },
      dark: {
        bg: semanticColors.dark.surfaceMain,
        bgHover: colors.strawberry20,
        bgActive: colors.strawberry24,
        bgDisabled: semanticColors.dark.surfaceMain,
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry52
      }
    },
    animated: {
      light: {
        bg: colors.strawberry8,
        bgHover: colors.strawberry8,
        bgActive: colors.strawberry16,
        bgDisabled: "transparent",
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry40
      },
      lightHighContrast: {
        bg: colors.strawberry8,
        bgHover: colors.strawberry8,
        bgActive: colors.strawberry16,
        bgDisabled: "transparent",
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry40
      },
      dark: {
        bg: colors.strawberry16,
        bgHover: colors.strawberry16,
        bgActive: colors.strawberry24,
        bgDisabled: "transparent",
        iconFill: colors.strawberry100,
        iconFillHover: colors.strawberry100,
        iconFillDisabled: colors.strawberry52
      }
    }
  }
};
const sizes = {
  tiny: 24,
  small: 36
};
const BG = styled.div`
  --absPos: ${({ $alignment }) => $alignment === "boundingBox" ? 0 : `calc(-1 * (${cssVars.size} - ${cssVars.iconSize}) / 2)`};
  position: absolute;
  top: var(--absPos);
  right: var(--absPos);
  bottom: var(--absPos);
  left: var(--absPos);
  overflow: hidden;
  width: ${cssVars.size};
  height: ${cssVars.size};
  border-radius: ${cssVars.borderRadius};

  transition: transform 96ms ease-in;
  z-index: 1;
`;
const IconContainer$1 = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: transparent;
  overflow: hidden;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Root$3 = styled.button`
  ${({ $alignment, $cssVarOverrides, $size }) => createCssVarDefinitions({
  size: `${sizes[$size]}px`,
  borderRadius: "50%",
  ...$cssVarOverrides,
  iconSize: `calc(${cssVars.size} / 2)`,
  width: $alignment === "boundingBox" ? cssVars.size : `calc(${cssVars.size} / 2)`
})}

  ${({ $background, $variant }) => createThemedCssVarDefinitions(
  themedConfigToThemedAttrs(variantConfigs$2[$variant][$background])
)}


  margin: 0;
  padding: 0;
  border: none;
  background: none;
  box-sizing: border-box;
  outline: none;

  position: relative;
  flex-shrink: 0;
  z-index: 0;

  width: ${cssVars.width};
  height: ${cssVars.width};
  border-radius: ${cssVars.borderRadius};

  &:disabled {
    cursor: not-allowed;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:not(:disabled) svg {
    fill: ${variantCssVars$2.iconFill};
  }

  ${BG} {
    background-color: ${variantCssVars$2.bg};
    transform: ${({ $background }) => $background !== "animated" ? "scale(1)" : "scale(0)"};
  }

  &:disabled ${BG} {
    background-color: ${variantCssVars$2.bgDisabled};
  }

  &:hover:not(:disabled) {
    ${BG} {
      background-color: ${variantCssVars$2.bgHover};
      transform: scale(1);
    }

    svg {
      fill: ${variantCssVars$2.iconFillHover};
    }
  }

  &:active:not(:disabled) ${BG} {
    background-color: ${variantCssVars$2.bgActive};
  }
  &:disabled svg {
    fill: ${variantCssVars$2.iconFillDisabled};
  }

  &:focus-visible ${BG} {
    box-shadow: 0 0 0 3px ${cbSemanticCssVars.a11yFocusIndicator};
    outline: 3px transparent solid;
    transform: scale(1);
  }

  svg {
    transition: fill 96ms ease-in;
    width: ${cssVars.iconSize};
    height: ${cssVars.iconSize};
  }
`;
const IconButton = React.forwardRef(
  ({
    alignment = "boundingBox",
    background = "static",
    children: icon,
    cssVarOverrides,
    size = "small",
    type = "button",
    variant = "blue",
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(
    Root$3,
    {
      $alignment: alignment,
      $background: background,
      $cssVarOverrides: cssVarOverrides,
      $size: size,
      $variant: variant,
      type,
      ...rest,
      ref,
      children: [
        /* @__PURE__ */ jsx(BG, { $alignment: alignment }),
        /* @__PURE__ */ jsx(IconContainer$1, { children: icon })
      ]
    }
  )
);
IconButton.displayName = "IconButton";
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const CaretDown = SvgCaretdown;
const isMac = typeof navigator !== "undefined" && navigator.platform.includes("Mac");
const KEYMAP = {
  Alt: isMac ? "⌥" : "Alt",
  CmdOrCtrl: isMac ? "⌘" : "Ctrl",
  Control: "Ctrl",
  Shift: "⇧ Shift",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→"
};
const MODIFIER_KEYS = {
  Alt: "altKey",
  Control: "ctrlKey",
  Meta: "metaKey",
  Shift: "shiftKey"
};
const variantConfigs$1 = {
  blue: {
    light: { ...variantConfigs$3.blue.light, middleBorder: colors.salt12 },
    dark: { ...variantConfigs$3.blue.dark, middleBorder: colors.salt16 }
  },
  lightBlue: {
    light: { ...variantConfigs$3.lightBlue.light, middleBorder: colors.wolt16 },
    dark: { ...variantConfigs$3.lightBlue.dark, middleBorder: colors.wolt16 }
  }
};
const kbdConfigs = {
  kbdBackground: { light: colors.salt92, dark: colors.pepper100 },
  kbdColor: { light: colors.pepper68, dark: colors.salt76 },
  kbdBorder: { light: colors.pepper20, dark: colors.pepper92 }
};
const toggleCssVarOverrides = {
  horizontalPadding: "16px"
};
const variantCssVars$1 = createCssVars(["background", "middleBorder"]);
const kbdCssVars = createCssVars(["kbdBackground", "kbdColor", "kbdBorder"]);
const Root$2 = styled.div`
  ${({ $variant }) => createThemedCssVarDefinitions(
  themedConfigToThemedAttrs(variantConfigs$1[$variant])
)}

  position: relative;
  z-index: 0;
  box-sizing: border-box;
  display: inline-flex;
  width: ${({ $stretch }) => $stretch ? "100%" : "auto"};
  max-width: 100%;
  border-radius: 8px;
`;
const PrimaryButton = styled(Button)`
  flex: 1;
  border-start-end-radius: 0;
  border-end-end-radius: 0;

  &:focus {
    z-index: 1;
  }
`;
const MiddleBorder = styled.div`
  width: 1px;
  background: ${variantCssVars$1.background};
  position: relative;

  &::after {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${variantCssVars$1.middleBorder};
  }

  &[data-disabled] {
    opacity: 0.8;
    cursor: not-allowed;
  }
`;
const Toggle = styled(Button)`
  position: relative;
  border-start-start-radius: 0;
  border-end-start-radius: 0;
  z-index: 0;

  svg {
    display: block;
    fill: currentColor;
    width: ${cssVars$1.iconWidth};
  }
`;
const defaultMenuPortalElement = getDefaultGlobalPopoverMountElement();
const Menu = styled(PopoverBase)`
  ${createThemedCssVarDefinitions(kbdConfigs)}
  min-width: 160px;
  max-width: 320px;
  padding: 8px 16px;
`;
const Kbd = styled.kbd`
  ${typographyCss.Small()}
  display: inline-block;
  padding: 2px 4px;
  border: ${kbdCssVars.kbdBorder} 1px solid;
  border-radius: 4px;
  background: ${kbdCssVars.kbdBackground};
  color: ${kbdCssVars.kbdColor};

  &:not(:first-child) {
    margin-inline-start: 4px;
  }
`;
const Shortcut = ({ keys }) => {
  const displayedKeys = (Array.isArray(keys) ? keys : [keys]).map((key) => {
    return key in KEYMAP ? KEYMAP[key] : key;
  });
  return /* @__PURE__ */ jsx(Fragment, { children: displayedKeys.map((key, index) => /* @__PURE__ */ jsx(Kbd, { children: key }, index)) });
};
const checkExactModifiersArePressed = (event, keyCodes) => {
  const modifiers = Object.keys(MODIFIER_KEYS);
  return !modifiers.some(
    (modifier) => keyCodes.includes(modifier) && !event[MODIFIER_KEYS[modifier]] || !keyCodes.includes(modifier) && event[MODIFIER_KEYS[modifier]]
  );
};
const matchesShortcut = (event, shortcut) => {
  if (!shortcut)
    return false;
  const keyCodes = (Array.isArray(shortcut) ? shortcut : [shortcut]).map((key) => {
    if (/^\d$/.test(key))
      return `Digit${key}`;
    if (/^[A-Z]$/.test(key))
      return `Key${key}`;
    if (key === "CmdOrCtrl")
      return isMac ? "Meta" : "Control";
    return key;
  });
  return keyCodes.includes(event.code) && checkExactModifiersArePressed(event, keyCodes);
};
const useKeyboardShortcuts = ({
  enabled,
  handleSelect,
  menuItems
}) => {
  useEffect(() => {
    const shortcutActions = menuItems.filter((item) => item.shortcut);
    if (enabled && shortcutActions.length) {
      const listener = (event) => {
        shortcutActions.forEach(({ onClick, shortcut }) => {
          if (matchesShortcut(event, shortcut))
            handleSelect(onClick);
        });
      };
      document.addEventListener("keydown", listener);
      return () => {
        document.removeEventListener("keydown", listener);
      };
    }
    return;
  }, [handleSelect, menuItems, enabled]);
};
const SplitButton = React.forwardRef(
  ({
    cssVarOverrides,
    disabled = false,
    keyboardShortcutHandlers = "global",
    menuButtonAriaLabel = "Other actions",
    menuItems,
    menuPortalElement,
    role: primaryButtonRole = "button",
    size = "small",
    stretch = false,
    style,
    type = "button",
    variant = "blue",
    ...rest
  }, ref) => {
    const rtl = useDirection() === "rtl";
    const [isFocused, setIsFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const listRef = useRef([]);
    const listContentRef = useRef(menuItems.map((item) => item.label));
    const isTypingRef = useRef(false);
    const { context, placement, refs, strategy, x, y } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: "bottom-end",
      middleware: [shift(), offset({ mainAxis: 4 }), flip()],
      whileElementsMounted: autoUpdate
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const listRole = useRole(context, { role: "menu" });
    const keyboardShortcutsEnabled = keyboardShortcutHandlers === "global" || keyboardShortcutHandlers === "whenInFocus" && isFocused;
    const listNav = useListNavigation(context, {
      listRef,
      activeIndex,
      selectedIndex,
      onNavigate: setActiveIndex,
      rtl
    });
    const placementSide = placement.split("-")[0];
    const typeahead = useTypeahead(context, {
      listRef: listContentRef,
      activeIndex,
      selectedIndex,
      onMatch: isOpen ? setActiveIndex : setSelectedIndex,
      onTypingChange(isTyping) {
        isTypingRef.current = isTyping;
      }
    });
    const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
      click,
      dismiss,
      listRole,
      listNav,
      typeahead
    ]);
    const { isMounted, styles } = usePopoverTransition(context);
    const handleSelect = useCallback((callback) => {
      setIsOpen(false);
      callback();
    }, []);
    useEffect(() => {
      if (!isOpen) {
        setActiveIndex(null);
      }
    }, [isOpen]);
    useEffect(() => {
      listContentRef.current = menuItems.map((item) => item.label);
    }, [menuItems]);
    useKeyboardShortcuts({ menuItems, enabled: keyboardShortcutsEnabled, handleSelect });
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        Root$2,
        {
          $stretch: stretch,
          $variant: variant,
          onBlur: () => setIsFocused(isOpen),
          onFocus: () => setIsFocused(true),
          style,
          children: [
            /* @__PURE__ */ jsx(
              PrimaryButton,
              {
                cssVarOverrides,
                disabled,
                role: primaryButtonRole,
                size,
                type,
                variant,
                ...rest,
                ref
              }
            ),
            /* @__PURE__ */ jsx(MiddleBorder, { "data-disabled": disabled ? true : void 0 }),
            /* @__PURE__ */ jsx(
              Toggle,
              {
                "aria-label": menuButtonAriaLabel,
                cssVarOverrides: { ...toggleCssVarOverrides, ...cssVarOverrides || {} },
                disabled,
                size,
                variant,
                ...getReferenceProps({ ref: refs.setReference }),
                children: /* @__PURE__ */ jsx(CaretDown, {})
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx(FloatingPortal, { root: menuPortalElement ?? defaultMenuPortalElement, children: isMounted && /* @__PURE__ */ jsx(FloatingFocusManager, { context, modal: false, children: /* @__PURE__ */ jsx(
        Menu,
        {
          style: {
            // floating-ui doesn't seem to resolve initial styles properly, thus this
            ...getClosedStyles({ side: placementSide }),
            ...styles,
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            overflowY: "auto"
          },
          ...getFloatingProps({ ref: refs.setFloating }),
          children: /* @__PURE__ */ jsx(MenuItemGroup, { children: menuItems.map(
            ({ disabled: disabled2, label, onClick, shortcut, variant: variant2 = "black", ...menuItemProps }, index) => /* @__PURE__ */ jsx(
              MenuActionItem,
              {
                variant: variant2,
                ...menuItemProps,
                "aria-current": index === activeIndex ? true : void 0,
                focused: index === activeIndex,
                ref: (node) => {
                  listRef.current[index] = node;
                },
                rightContent: shortcut && /* @__PURE__ */ jsx(Shortcut, { keys: shortcut }),
                role: "menuitem",
                tabIndex: index === activeIndex ? 0 : -1,
                ...getItemProps({
                  disabled: disabled2,
                  // Handle pointer select.
                  onClick() {
                    handleSelect(onClick);
                  },
                  // Handle keyboard select.
                  onKeyDown(event) {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (!keyboardShortcutsEnabled || menuItems.every(
                        (item) => !matchesShortcut(
                          // TODO: remove type assertion once React 16 support is dropped, see https://github.com/facebook/react/pull/18287
                          event,
                          item.shortcut
                        )
                      )) {
                        handleSelect(onClick);
                      }
                    }
                    if (event.key === " " && !isTypingRef.current) {
                      event.preventDefault();
                    }
                  },
                  onKeyUp(event) {
                    if (event.key === " " && !isTypingRef.current) {
                      handleSelect(onClick);
                    }
                  }
                }),
                children: label
              },
              index
            )
          ) })
        }
      ) }) })
    ] });
  }
);
SplitButton.displayName = "SplitButton";
const variantCssVars = createCssVars(["color", "colorHover", "colorActive", "colorDisabled"]);
const variantConfigs = {
  blue: {
    light: {
      color: colors.wolt100,
      colorHover: colors.wolt92,
      colorActive: shade(0.15)(colors.wolt100),
      colorDisabled: colors.wolt40
    },
    lightHighContrast: {
      color: colors.darkSky100,
      colorHover: colors.darkSky92,
      colorActive: shade(0.15)(colors.darkSky100),
      colorDisabled: colors.darkSky72
    },
    dark: {
      color: colors.wolt100,
      colorHover: colors.wolt92,
      colorActive: colors.wolt76,
      colorDisabled: colors.wolt48
    }
  },
  black: {
    light: {
      color: colors.pepper100,
      colorHover: colors.pepper92,
      colorActive: colors.pepper84,
      colorDisabled: colors.pepper52
    },
    lightHighContrast: {
      color: colors.pepper100,
      colorHover: colors.pepper92,
      colorActive: colors.pepper84,
      colorDisabled: colors.pepper52
    },
    dark: {
      color: colors.salt100,
      colorHover: colors.salt92,
      colorActive: colors.salt80,
      colorDisabled: colors.salt60
    }
  },
  grey: {
    light: {
      color: colors.pepper64,
      colorHover: colors.pepper56,
      colorActive: colors.pepper76,
      colorDisabled: colors.pepper32
    },
    lightHighContrast: {
      color: colors.pepper64,
      colorHover: colors.pepper56,
      colorActive: colors.pepper76,
      colorDisabled: colors.pepper32
    },
    dark: {
      color: colors.salt72,
      colorHover: colors.salt64,
      colorActive: colors.salt52,
      colorDisabled: colors.salt32
    }
  },
  lightGrey: {
    light: {
      color: colors.pepper48,
      colorHover: colors.pepper40,
      colorActive: colors.pepper60,
      colorDisabled: colors.pepper20
    },
    lightHighContrast: {
      color: colors.pepper48,
      colorHover: colors.pepper40,
      colorActive: colors.pepper60,
      colorDisabled: colors.pepper20
    },
    dark: {
      color: colors.salt60,
      colorHover: colors.salt52,
      colorActive: colors.salt48,
      colorDisabled: colors.salt28
    }
  },
  white: {
    light: {
      color: colors.salt100,
      colorHover: colors.salt92,
      colorActive: colors.salt80,
      colorDisabled: colors.salt64
    },
    lightHighContrast: {
      color: colors.salt100,
      colorHover: colors.salt92,
      colorActive: colors.salt80,
      colorDisabled: colors.salt64
    },
    dark: {
      color: colors.salt100,
      colorHover: colors.salt92,
      colorActive: colors.salt80,
      colorDisabled: colors.salt64
    }
  },
  lightWhite: {
    light: {
      color: colors.salt56,
      colorHover: colors.salt48,
      colorActive: colors.salt44,
      colorDisabled: colors.salt28
    },
    lightHighContrast: {
      color: colors.salt56,
      colorHover: colors.salt48,
      colorActive: colors.salt44,
      colorDisabled: colors.salt28
    },
    dark: {
      color: colors.salt56,
      colorHover: colors.salt48,
      colorActive: colors.salt44,
      colorDisabled: colors.salt28
    }
  },
  red: {
    light: {
      color: colors.strawberry100,
      colorHover: colors.strawberry92,
      colorActive: shade(0.15)(colors.strawberry100),
      colorDisabled: colors.strawberry72
    },
    lightHighContrast: {
      color: colors.strawberry100,
      colorHover: colors.strawberry92,
      colorActive: shade(0.15)(colors.strawberry100),
      colorDisabled: colors.strawberry72
    },
    dark: {
      color: colors.strawberry100,
      colorHover: colors.strawberry92,
      colorActive: colors.strawberry76,
      colorDisabled: colors.strawberry72
    }
  },
  yellow: {
    light: {
      color: colors.banana100,
      colorHover: colors.banana92,
      colorActive: shade(0.15)(colors.banana100),
      colorDisabled: colors.banana72
    },
    lightHighContrast: {
      color: shade(0.15)(colors.banana100),
      colorHover: shade(0.15)(colors.banana92),
      colorActive: shade(0.25)(colors.banana100),
      colorDisabled: shade(0.15)(colors.banana72)
    },
    dark: {
      color: colors.banana100,
      colorHover: colors.banana92,
      colorActive: colors.banana76,
      colorDisabled: colors.banana72
    }
  },
  orange: {
    light: {
      color: colors.orange100,
      colorHover: colors.orange92,
      colorActive: shade(0.15)(colors.orange100),
      colorDisabled: colors.orange72
    },
    lightHighContrast: {
      color: shade(0.15)(colors.orange100),
      colorHover: shade(0.15)(colors.orange92),
      colorActive: shade(0.25)(colors.orange100),
      colorDisabled: shade(0.15)(colors.orange72)
    },
    dark: {
      color: colors.orange100,
      colorHover: colors.orange92,
      colorActive: colors.orange76,
      colorDisabled: colors.orange72
    }
  },
  green: {
    light: {
      color: colors.lime100,
      colorHover: colors.lime92,
      colorActive: shade(0.15)(colors.lime100),
      colorDisabled: colors.lime72
    },
    lightHighContrast: {
      color: shade(0.15)(colors.lime100),
      colorHover: shade(0.15)(colors.lime92),
      colorActive: shade(0.25)(colors.lime100),
      colorDisabled: shade(0.15)(colors.lime72)
    },
    dark: {
      color: colors.lime100,
      colorHover: colors.lime92,
      colorActive: colors.lime76,
      colorDisabled: colors.lime72
    }
  }
};
const sizeCssMap = {
  inherit: css`
    ${typographyCss.baseFontFamily()}
    font-size: inherit;
    line-height: inherit;
  `,
  small: typographyCss.Body3(),
  medium: typographyCss.Body2(),
  large: typographyCss.Body1()
};
const iconSizeConfigs = {
  inherit: "0.75em",
  small: "12px",
  medium: "14px",
  large: "16px"
};
const sizeCssVars = createCssVars(["iconSize"]);
const rootCss = css`
  ${({ $variant }) => createThemedCssVarDefinitions(
  themedConfigToThemedAttrs(variantConfigs[$variant])
)}

  ${({ $cssVarOverrides, $size }) => createCssVarDefinitions({
  iconSize: iconSizeConfigs[$size],
  ...$cssVarOverrides
})}

  margin: 0;
  padding: 0;
  border: none;
  background: none;
  position: relative;
  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  /* stylelint-disable font-family-no-missing-generic-family-keyword */
  font-family: ${({ $fontFamily }) => $fontFamily === "inherit" ? "inherit" : `-apple-system, BlinkMacSystemFont, Roboto, 'Segoe UI', Arimo, 'Open Sans', 'Helvetica Neue', sans-serif`};
  /* stylelint-enable font-family-no-missing-generic-family-keyword */

  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: ${variantCssVars.color};
  ${({ $size }) => sizeCssMap[$size]}
  font-weight: 500;

  user-select: ${({ $disableTextSelect }) => $disableTextSelect ? "none" : "inherit"};

  white-space: ${({ $wrapText }) => $wrapText ? "normal" : "nowrap"};

  &:disabled {
    cursor: not-allowed;
    color: ${variantCssVars.colorDisabled};
    svg {
      fill: ${variantCssVars.colorDisabled};
    }
  }

  &:not(:disabled):hover {
    color: ${variantCssVars.colorHover};
    svg {
      fill: ${variantCssVars.colorHover};
    }
  }

  &:not(:disabled):active {
    color: ${variantCssVars.colorActive};
    svg {
      fill: ${variantCssVars.colorActive};
    }
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: 3px transparent solid;
    box-shadow: 0 0 0 3px ${cbSemanticCssVars.a11yFocusIndicator};
    border-radius: 2px;
  }
`;
const Root$1 = styled.button`
  ${rootCss}
`;
const DisabledButtonOnMouseLeaveFix = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
const IconContainer = styled.span`
  display: inline-block;
  flex-shrink: 0;
  line-height: 0;

  margin-inline-start: ${({ $position }) => $position === "left" ? 0 : 8}px;
  margin-inline-end: ${({ $position }) => $position === "right" ? 0 : 8}px;

  > svg {
    fill: ${variantCssVars.color};
    width: ${sizeCssVars.iconSize};
  }
`;
const TextButtonContent = ({
  children,
  icon,
  iconPosition
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  !!icon && iconPosition === "left" && /* @__PURE__ */ jsx(IconContainer, { $position: iconPosition, children: icon }),
  children,
  !!icon && iconPosition === "right" && /* @__PURE__ */ jsx(IconContainer, { $position: iconPosition, children: icon })
] });
const TextButton = React.forwardRef(
  ({
    children,
    cssVarOverrides,
    disabled,
    disableTextSelect = false,
    fontFamily = "default",
    icon,
    iconPosition = "left",
    size = "small",
    type = "button",
    variant = "blue",
    wrap = false,
    ...restButtonProps
  }, ref) => /* @__PURE__ */ jsxs(
    Root$1,
    {
      $cssVarOverrides: cssVarOverrides,
      $disableTextSelect: disableTextSelect,
      $fontFamily: fontFamily,
      $size: size,
      $variant: variant,
      $wrapText: wrap,
      disabled,
      type,
      ...restButtonProps,
      ref,
      children: [
        /* @__PURE__ */ jsx(DisabledButtonOnMouseLeaveFix, {}),
        /* @__PURE__ */ jsx(TextButtonContent, { icon, iconPosition, children })
      ]
    }
  )
);
TextButton.displayName = "TextButton";
const Root = styled.a`
  ${rootCss}
  text-decoration: none;
`;
const TextButtonLink = React.forwardRef(
  ({
    children,
    cssVarOverrides,
    disableTextSelect = false,
    fontFamily = "default",
    icon,
    iconPosition = "left",
    size = "small",
    variant = "blue",
    wrap = true,
    ...rest
  }, ref) => /* @__PURE__ */ jsx(
    Root,
    {
      $cssVarOverrides: cssVarOverrides,
      $disableTextSelect: disableTextSelect,
      $fontFamily: fontFamily,
      $size: size,
      $variant: variant,
      $wrapText: wrap,
      ...rest,
      ref,
      children: /* @__PURE__ */ jsx(TextButtonContent, { icon, iconPosition, children })
    }
  )
);
TextButtonLink.displayName = "TextButtonLink";
export {
  Button,
  IconButton,
  SplitButton,
  TextButton,
  TextButtonLink
};
//# sourceMappingURL=index.js.map
